import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FileUploader } from "react-drag-drop-files";
import { sendRequest } from "../../apis/APIs";
import { handleApiResponse } from "../../apis/apiUtils";
import { callCommonAction } from '../../redux/Common/CommonReducer';
import { ValidationUserDoc } from './ValidationUserDoc';  // Custom validation

const UserDocuments = ({ showUserDocumentPopup, setShowUserDocumentPopup, handleClick, docId }) => {
    const [data, setData] = useState({
        title: '',
        file: null,
    });
    const [existingFilePath, setExistingFilePath] = useState('');
    const [existingFileFullUrl, setExistingFileFullUrl] = useState('');
    const [fullUrl, setFullUrl] = useState('');

    const [isUpdate, setIsUpdate] = useState(false);
    const [errorsInfo, setErrorsInfo] = useState({});
    const dispatch = useDispatch();
    const { loading, user } = useSelector((state) => state.common);

    useEffect(() => {
        if (docId) {
            setIsUpdate(true);
            fetchDocumentData(docId);
        } else {
            setIsUpdate(false);
            setData({ title: '', file: null });
            setExistingFilePath('');
        }
    }, [docId]);

    const fetchDocumentData = async (docId) => {
        try {
            const res = await sendRequest(`/user/documents-get/${docId}`, 'GET');
            if (res.data.type === 'success') {
                const doc = res.data.data;
                setData({
                    title: doc.title,
                    file: null
                });
                setExistingFileFullUrl(res?.data?.data?.file_type)
                setExistingFilePath(doc?.file_path);
                setFullUrl(doc?.full_file_path)
            }
        } catch (error) {
            console.error("Failed to fetch document data:", error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setData((prev) => ({ ...prev, [name]: value }));
        setErrorsInfo((prev) => ({ ...prev, [name]: '' }));
    };

    const checkFormIsValid = (fieldName) => {
        const res = ValidationUserDoc(data, fieldName, docId);
        setErrorsInfo(res.errors);
        return res.formValidCheck;
    };

    const handleSave = async (e) => {
        e.preventDefault();

        try {
            if (checkFormIsValid('')) {
                dispatch(callCommonAction({ loading: true }));

                const formData = new FormData();
                formData.append("title", data.title);
                formData.append("id", user?.id);

                if (data?.file) {
                    const fileNameWithExt = data?.file?.name;
                    const ext = fileNameWithExt?.split('.').pop();
                    const fileName = fileNameWithExt?.split('.').slice(0, -1).join('.');
                    const formattedFileName = fileName?.replace(/\s+/g, '_');
                    const newFileName = `${formattedFileName}.${ext}`;
                    const newFile = new File([data?.file], newFileName, { type: data?.file?.type });
                    formData.append("file", newFile);
                }
                else if (isUpdate && existingFilePath) {
                    // If it's an update and no new file is provided, use the current file path to refer to the existing file
                    formData.append("filePath", existingFilePath); // Assuming file_path is the URL of the existing file
                }
                let res;
                if (isUpdate) {
                    formData.append("type", 3); // required field for updates
                    res = await sendRequest(`/user/document/${docId}`, 'POST', formData, {
                        "Content-Type": "multipart/form-data",
                    });

                } else {
                    res = await sendRequest('/user/user-documents', 'POST', formData,{
                          "Content-Type": "multipart/form-data",
                    });
                }

                dispatch(callCommonAction({ loading: false }));
                handleApiResponse(res);

                if (res.data.type === 'success') {
                    setShowUserDocumentPopup(false);
                    handleClick("myDocs");
                }
            }
        } catch (error) {
            console.error("Error:", error);
            dispatch(callCommonAction({ loading: false }));
        }
    };


    const handleFileChange = (file) => {
        setData((prev) => ({ ...prev, file }));
        setErrorsInfo((prev) => ({ ...prev, file: '' }));
        setExistingFilePath('');
        setFullUrl('')
    };
    const getFileTypeIcon = (fileType) => {
        console.log(fileType, "fileTypefileTypefileType");
        const iconStyle = { fontSize: '74px' }; // Adjust as needed

        switch (fileType) {
            case "pdf":
                return <iconify-icon icon="material-icon-theme:pdf" style={iconStyle}></iconify-icon>;
            case "excel":
            case "xlsx":
            case "xls":
                return <iconify-icon icon="vscode-icons:file-type-excel" style={iconStyle}></iconify-icon>;
            case "word":
            case "docx":
            case "doc":
                return <iconify-icon icon="vscode-icons:file-type-word" style={iconStyle}></iconify-icon>;
            default:
                return <iconify-icon icon="material-icon-theme:pdf" style={iconStyle}></iconify-icon>;
        }
    };


    return (
        <Modal show={showUserDocumentPopup} onHide={() => setShowUserDocumentPopup(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title className="fs-6">{isUpdate ? 'Update Document' : 'Add Document'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={(e) => e.preventDefault()}>
                    <Form.Group className="mb-3">
                        <Form.Label>Title*</Form.Label>
                        <Form.Control
                            type="text"
                            name="title"
                            placeholder="Enter Title"
                            value={data.title}
                            onChange={handleChange}
                        />
                        {errorsInfo.title && (
                            <div className="error-message small" style={{ color: 'red' }}>{errorsInfo.title}</div>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Choose Document*</Form.Label>
                        <FileUploader
                            handleChange={handleFileChange}
                            name="file"
                            multiple={false}
                            types={["PDF", "XLSX", "DOCX"]}
                            maxSize={1000000}
                        />
                        <small className="text-muted">Drag & drop files here</small>
                        {errorsInfo.file && (
                            <div className="error-message small" style={{ color: 'red' }}>{errorsInfo.file}</div>
                        )}

                        {(existingFilePath) && (
                            <>
                                {/* {getFileTypeIcon(existingFileFullUrl)} */}

                                <div style={{ width: '100px', height: 'auto' }}>
                                    <span className="d-block mb-2 mt-1 fw-semibold">Existing File</span>
                                    <a
                                        href={fullUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ display: 'inline-block' }}
                                    >
                                        {getFileTypeIcon(existingFileFullUrl)}
                                    </a>
                                </div>


                            </>
                        )}
                    </Form.Group>
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <button
                    onClick={() => setShowUserDocumentPopup(false)}
                    className="btn btn-outline-custom btn-sm px-3 py-2 rounded-3"
                >
                    Close
                </button>
                <button
                    type="submit"
                    onClick={handleSave}
                    disabled={loading}
                    className="btn btn-outline-custom active btn-sm d-inline-flex px-3 py-2 rounded-3"
                >
                    {loading ? (
                        <span className="indicator-progress" style={{ display: 'block' }}>
                            Please wait...
                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                    ) : (
                        <span className="text-uppercase d-flex align-items-center">
                            <iconify-icon icon="tdesign:save" class="fs-5 me-1"></iconify-icon>
                            {isUpdate ? 'Update' : 'Save'}
                        </span>
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default UserDocuments;
